import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { canonicalUrl } from '@/lib/canonical';

const META_TAGS = [
  // [attribute, key, value-prop]
  { sel: 'meta[name="description"]', attr: { name: 'description' }, valueKey: 'content', from: 'description' },
  { sel: 'meta[property="og:title"]', attr: { property: 'og:title' }, valueKey: 'content', from: 'title' },
  { sel: 'meta[property="og:description"]', attr: { property: 'og:description' }, valueKey: 'content', from: 'description' },
  { sel: 'meta[property="og:type"]', attr: { property: 'og:type' }, valueKey: 'content', from: 'ogType', default: 'website' },
  { sel: 'meta[property="og:url"]', attr: { property: 'og:url' }, valueKey: 'content', from: 'url' },
  { sel: 'meta[property="og:image"]', attr: { property: 'og:image' }, valueKey: 'content', from: 'ogImage' },
  { sel: 'meta[property="og:site_name"]', attr: { property: 'og:site_name' }, valueKey: 'content', from: 'siteName', default: 'MobileCoreX' },
  { sel: 'meta[name="twitter:card"]', attr: { name: 'twitter:card' }, valueKey: 'content', from: 'twitterCard', default: 'summary_large_image' },
  { sel: 'meta[name="twitter:title"]', attr: { name: 'twitter:title' }, valueKey: 'content', from: 'title' },
  { sel: 'meta[name="twitter:description"]', attr: { name: 'twitter:description' }, valueKey: 'content', from: 'description' },
  { sel: 'meta[name="twitter:image"]', attr: { name: 'twitter:image' }, valueKey: 'content', from: 'ogImage' },
];

function upsertMeta(spec, value) {
  let el = document.head.querySelector(spec.sel);
  if (!el) {
    el = document.createElement('meta');
    Object.entries(spec.attr).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute(spec.valueKey, value);
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Apply per-page metadata (title + Open Graph + Twitter Card).
 *
 * @param {object} meta
 * @param {string} meta.title
 * @param {string} meta.description
 * @param {string} [meta.ogImage]   Absolute or root-relative URL.
 * @param {string} [meta.ogType]    'website' (default) | 'article' | 'profile'
 * @param {string} [meta.url]       Canonical absolute URL.
 */
export default function usePageMeta(meta) {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!meta || !meta.title) return undefined;

    const fullTitle = meta.title.includes('MobileCoreX') ? meta.title : `${meta.title} — MobileCoreX`;
    document.title = fullTitle;

    const computed = {
      title: fullTitle,
      description: meta.description || '',
      ogType: meta.ogType,
      url: meta.url || canonicalUrl(pathname),
      ogImage: meta.ogImage,
      siteName: undefined,
      twitterCard: undefined,
    };

    META_TAGS.forEach((spec) => {
      const value = computed[spec.from] ?? spec.default;
      if (value) upsertMeta(spec, value);
    });

    upsertCanonical(computed.url);

    return undefined;
  }, [meta]);
}
